/* Desktop uses a finite native scroll rail. The original carousel owns mobile. */
(() => {
  class DesktopProductRail {
    constructor(host) {
      this.host = host;
      this.track = host.querySelector('.product-carousel__track');
      this.items = [...this.track.children];
      this.previous = host.querySelector('[data-carousel-prev]');
      this.next = host.querySelector('[data-carousel-next]');
      this.status = host.querySelector('.product-carousel__status');
      this.progress = host.querySelector('.product-carousel__progress');
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.previous.addEventListener('click', () => this.step(-1), options);
      this.next.addEventListener('click', () => this.step(1), options);
      this.track.addEventListener('scroll', () => {
        if (!this.scrollFrame) this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = null; this.update(); });
        clearTimeout(this.idleTimer);
        this.idleTimer = setTimeout(() => { this.target = null; }, 160);
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.target.closest('input,select,textarea')) return;
        event.preventDefault();
        this.step((event.key === 'ArrowRight' ? 1 : -1) * this.sign);
      }, options);
      this.track.addEventListener('wheel', () => { this.target = null; }, { ...options, passive: true });
      this.track.addEventListener('dragstart', event => event.preventDefault(), options);
      this.track.addEventListener('pointerdown', event => {
        this.target = null;
        if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('button,input,select,textarea')) return;
        this.drag = { x: event.clientX, start: this.position, id: event.pointerId, moved: false };
      }, options);
      this.track.addEventListener('pointermove', event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const delta = (this.drag.x - event.clientX) * this.sign;
        if (!this.drag.moved && Math.abs(delta) < 6) return;
        this.drag.moved = true;
        this.suppressClick = true;
        this.host.setAttribute('data-desktop-dragging', '');
        this.track.setPointerCapture(event.pointerId);
        this.track.scrollLeft = this.clamp(this.drag.start + delta) * this.sign;
      }, options);
      const release = event => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const moved = this.drag.moved;
        this.drag = null;
        this.host.removeAttribute('data-desktop-dragging');
        if (moved) this.go(this.stops.reduce((best, stop) => Math.abs(stop - this.position) < Math.abs(best - this.position) ? stop : best));
        clearTimeout(this.clickTimer);
        this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 0);
      };
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach(type => this.track.addEventListener(type, release, options));
      this.track.addEventListener('click', event => {
        if (this.suppressClick) { event.preventDefault(); event.stopPropagation(); }
      }, { ...options, capture: true });
      this.observer = new ResizeObserver(() => {
        cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = requestAnimationFrame(() => this.measure());
      });
      this.observer.observe(this.track);
      this.measure();
    }
    get position() { return this.track.scrollLeft * this.sign; }
    clamp(value) { return Math.max(0, Math.min(this.max, value)); }
    measure() {
      this.sign = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      const rect = this.track.getBoundingClientRect();
      this.max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const position = this.position;
      const stops = this.items.map(item => {
        const box = item.getBoundingClientRect();
        return this.clamp(position + (this.sign === 1 ? box.left - rect.left - 12 : rect.right - box.right - 12));
      });
      // Clamp the last page once; several cards may share the same end stop.
      this.stops = [0, ...stops, this.max].sort((a, b) => a - b).filter((stop, i, all) => !i || stop - all[i - 1] > 1);
      this.host.toggleAttribute('data-desktop-static', this.max <= 1);
      this.host.style.setProperty('--carousel-arrows-y', `${this.track.offsetTop + this.track.offsetHeight / 2}px`);
      const navigation = this.host.dataset.navigationSplit === 'true' ? this.host.dataset.navigationDesktop : this.host.dataset.navigation || 'progress';
      this.host.querySelector('.product-carousel__controls').hidden = this.max <= 1 || !['arrows', 'both'].includes(navigation);
      if (this.progress) {
        this.progress.hidden = this.max <= 1 || !['progress', 'both'].includes(navigation);
        this.progress.setAttribute('aria-valuemin', '0');
        this.progress.setAttribute('aria-valuemax', '100');
      }
      this.target = null;
      this.update();
    }
    update() {
      const position = this.clamp(this.position);
      this.previous.disabled = position <= 1;
      this.next.disabled = position >= this.max - 1;
      const index = this.stops.reduce((best, stop, i) => Math.abs(stop - position) < Math.abs(this.stops[best] - position) ? i : best, 0);
      const label = `${index + 1} / ${this.stops.length}`;
      if (this.status.textContent !== label) this.status.textContent = label;
      if (this.progress) {
        const value = this.max > 1 ? position / this.max : 1;
        this.progress.style.setProperty('--carousel-progress', value.toFixed(5));
        this.progress.setAttribute('aria-valuenow', Math.round(value * 100));
      }
    }
    go(position) {
      this.target = this.clamp(position);
      this.track.scrollTo({ left: this.target * this.sign, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
    step(direction) {
      const position = this.target ?? this.position;
      const stop = direction > 0 ? this.stops.find(value => value > position + 1)
        : [...this.stops].reverse().find(value => value < position - 1);
      if (stop !== undefined) this.go(stop);
    }
    disconnect() {
      this.controller.abort();
      this.observer.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.layoutFrame);
      clearTimeout(this.idleTimer);
      clearTimeout(this.clickTimer);
      if (this.drag && this.track.hasPointerCapture(this.drag.id)) this.track.releasePointerCapture(this.drag.id);
    }
  }
  customElements.whenDefined('product-carousel').then(() => {
    if (customElements.get('responsive-product-carousel')) return;
    const MobileCarousel = customElements.get('product-carousel');
    customElements.define('responsive-product-carousel', class extends MobileCarousel {
      connectedCallback() {
        if (this.modeController) return;
        const track = this.querySelector('.product-carousel__track');
        if (!track?.children.length) {
          this.connectFrame = requestAnimationFrame(() => {
            if (this.isConnected && this.querySelector('.product-carousel__track')?.children.length) this.connectedCallback();
          });
          return;
        }
        this.originalItems = [...track.children];
        this.desktopViewport = matchMedia('(min-width: 750px)');
        this.modeController = new AbortController();
        this.desktopViewport.addEventListener('change', () => this.switchMode(), { signal: this.modeController.signal });
        this.switchMode();
      }
      switchMode() {
        if (this.desktop) this.desktop.disconnect();
        else if (this.mode === 'mobile') super.disconnectedCallback();
        this.desktop = null;
        this.mode = this.desktopViewport.matches ? 'desktop' : 'mobile';
        const track = this.querySelector('.product-carousel__track');
        // Reset only at a breakpoint, never while scrolling. Restore original
        // order after the mobile controller may have recycled its own slides.
        if (this.originalItems.some((item, i) => track.children[i] !== item)) {
          this.originalItems.forEach(item => {
            if (track.moveBefore) track.moveBefore(item, null);
            else track.append(item);
          });
        }
        ['data-ready', 'data-animating', 'data-repositioning', 'data-dragging', 'data-desktop-dragging', 'data-desktop-static'].forEach(name => this.removeAttribute(name));
        this.originalItems.forEach(item => { item.removeAttribute('data-active'); item.removeAttribute('data-near'); item.style.removeProperty('--carousel-scale'); });
        this.target = null;
        this.drag = null;
        this.lastWidth = null;
        this.setAttribute('data-desktop-rail', this.mode === 'desktop' ? 'true' : 'false');
        track.scrollTo({ left: 0, behavior: 'instant' });
        if (this.mode === 'desktop') this.desktop = new DesktopProductRail(this);
        else super.connectedCallback();
      }
      disconnectedCallback() {
        cancelAnimationFrame(this.connectFrame);
        this.modeController?.abort();
        this.modeController = null;
        if (this.desktop) this.desktop.disconnect();
        else if (this.mode === 'mobile') super.disconnectedCallback();
        this.desktop = null;
        this.mode = null;
      }
    });
  });
})();
